import { InboundPromoCodesService } from './services/InboundPromoCodesService';
import type { InboundPromoCodesInit, InboundPromoCodesUrlAdapter } from './types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

type TApi = ReturnType<InboundPromoCodesService['api']>;

/**
 * Responsible for capturing promo codes from inbound sources:
 * - URL params (`promoCode`, `promotionCd`)
 * - Partner IDs (mapped to promo codes via Statsig)
 *
 * Emits captured codes via `inboundPromoCodesEventBus` for downstream coordination
 * by controllers. Does NOT validate or apply codes itself.
 */
export class InboundPromoCodesManager extends WithApi<TApi> {
  private inboundPromoCodesPageStore: InboundPromoCodesInit['pageStore'] | null = null;

  constructor(private urlAdapter: InboundPromoCodesUrlAdapter) {
    super();
  }

  public getInboundPromoCodesFromStore(): string[] {
    return this.inboundPromoCodesPageStore?.getState().inboundPromoCodes ?? [];
  }

  public async getInboundPromoCodesWhenReady(): Promise<string[]> {
    await this.getApiWhenReady();

    return this.getInboundPromoCodesFromStore();
  }

  public inboundPromoCodesInit = ({ pageStore, queries, paramStore }: InboundPromoCodesInit) => {
    this.inboundPromoCodesPageStore = pageStore;

    const [customerQuery] = queries;

    const service = new InboundPromoCodesService(
      pageStore,
      this.urlAdapter,
      paramStore.getPartnerIdToPromoCode(),
      customerQuery.data
    );

    this.initApi({
      ...service.api(),
    });

    const { isPending } = customerQuery;
    const { isError } = customerQuery;

    return {
      status: this.getStatus(isPending, isError),
      data: {
        ...service.data(),
      },
      handlers: {
        ...service.handlers(),
      },
      lifecycle: {
        onMount() {
          service.onMount();
        },
      } as WidgetLifecycle,
    } as const;
  };

  private getStatus(isPending: boolean, isError: boolean): 'pending' | 'error' | 'loaded' {
    if (isPending) {
      return 'pending';
    }

    if (isError) {
      return 'error';
    }

    return 'loaded';
  }
}
